import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { endOfDay, format } from 'date-fns';
import { getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { Calendar, type View, dateFnsLocalizer } from 'react-big-calendar';

import type { ScheduleCalendarEvent, ScheduleSummary, TriggerDetail } from '@/types/schedule';

import {
  calendarVisibleRangeToApiInstants,
  instantToCalendarDisplayDate,
  parseTriggerDetailInstant,
  preserveCalendarDayOnModeSwitch,
  utcCalendarToday,
} from '../utils/scheduleDateTime';
import { type TimeDisplayMode } from './shared/TimeDisplayToggle';

// Set up date-fns localizer for react-big-calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface ScheduleCalendarViewProps {
  schedules: ScheduleSummary[];
  executionDetails: TriggerDetail[];
  /** Client-generated upcoming schedule rows; only populated when the toggle is on. */
  plannedExecutionDetails?: TriggerDetail[];
  /** When false, legend omits Scheduled and only completed executions render. */
  showUpcomingSchedules?: boolean;
  isLoading?: boolean;
  onEventClick?: (event: ScheduleCalendarEvent) => void;
  onDateChange?: (start: Date, end: Date) => void;
  selectedEnvironmentId?: string;
  timeMode?: TimeDisplayMode;
}

// Color palette for different statuses
const STATUS_COLORS = {
  Succeeded: {
    background: '#e8f5e9',
    border: '#4caf50',
    text: '#1b5e20',
  },
  Failed: {
    background: '#ffebee',
    border: '#f44336',
    text: '#b71c1c',
  },
  Running: {
    background: '#e3f2fd',
    border: '#2196f3',
    text: '#0d47a1',
  },
  Scheduled: {
    background: '#e3f2fd',
    border: '#2196f3',
    text: '#0d47a1',
  },
  Paused: {
    background: '#f5f5f5',
    border: '#9e9e9e',
    text: '#616161',
  },
  default: {
    background: '#f5f5f5',
    border: '#9e9e9e',
    text: '#616161',
  },
} as const;

const CALENDAR_LEGEND_STATUSES = ['Succeeded', 'Failed', 'Running', 'Scheduled', 'Paused'] as const;

export const ScheduleCalendarView = ({
  schedules,
  executionDetails,
  plannedExecutionDetails = [],
  showUpcomingSchedules = false,
  isLoading = false,
  onEventClick,
  onDateChange,
  selectedEnvironmentId,
  timeMode = 'local',
}: ScheduleCalendarViewProps) => {
  const legendStatuses = showUpcomingSchedules
    ? CALENDAR_LEGEND_STATUSES
    : CALENDAR_LEGEND_STATUSES.filter((status) => status !== 'Scheduled');

  const [currentView, setCurrentView] = useState<View>('week');
  const [currentDate, setCurrentDate] = useState(() =>
    timeMode === 'utc' ? utcCalendarToday() : new Date()
  );
  const lastVisibleRangeRef = useRef<{ start: Date; end: Date } | null>(null);

  const emitApiRange = useCallback(
    (start: Date, end: Date) => {
      const apiRange = calendarVisibleRangeToApiInstants(start, endOfDay(end));
      onDateChange?.(apiRange.start, apiRange.end);
    },
    [onDateChange]
  );

  // Preserve the viewed day when toggling Local ↔ UTC (display only — no API refetch).
  useEffect(() => {
    setCurrentDate((prev) => preserveCalendarDayOnModeSwitch(prev));
  }, [timeMode]);

  // Filter by environment if specified
  const filteredSchedules = useMemo(() => {
    if (!selectedEnvironmentId) return schedules;
    return schedules.filter((s) => String(s.environmentId) === String(selectedEnvironmentId));
  }, [schedules, selectedEnvironmentId]);

  const filteredDetails = useMemo(() => {
    if (!selectedEnvironmentId) return executionDetails;
    return executionDetails.filter(
      (d) => String(d.environmentId) === String(selectedEnvironmentId)
    );
  }, [executionDetails, selectedEnvironmentId]);

  const filteredPlannedDetails = useMemo(() => {
    if (!plannedExecutionDetails.length) return [];
    if (!selectedEnvironmentId) return plannedExecutionDetails;
    return plannedExecutionDetails.filter(
      (d) => String(d.environmentId) === String(selectedEnvironmentId)
    );
  }, [plannedExecutionDetails, selectedEnvironmentId]);

  // Point-in-time events — matches legacy dataProtect ScheduleCalendarView (start === end).
  // Using 30-minute windows caused every rate(5 min) slot to overlap and fan out into
  // many parallel columns via dayLayoutAlgorithm="no-overlap".
  const buildEventWindow = (start: Date): { start: Date; end: Date } => ({
    start,
    end: start,
  });

  const calendarFormats = useMemo(
    () => ({
      timeGutterFormat: (date: Date) => format(date, 'h:mm a'),
      eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
    }),
    []
  );

  const calendarEvents = useMemo((): ScheduleCalendarEvent[] => {
    const events: ScheduleCalendarEvent[] = [];

    const appendDetailEvent = (detail: TriggerDetail, idPrefix: string, index: number) => {
      const instant = parseTriggerDetailInstant(detail, filteredSchedules);
      if (!instant) return;

      const startDate = instantToCalendarDisplayDate(instant, timeMode);
      if (isNaN(startDate.getTime())) return;
      const { start, end } = buildEventWindow(startDate);

      events.push({
        id:
          detail.externalExecutionId ||
          `${idPrefix}-${detail.baseId}-${instant.getTime()}-${index}-${timeMode}`,
        title: detail.transformerName,
        start,
        end,
        allDay: false,
        resource: {
          baseId: detail.baseId,
          environmentId: detail.environmentId,
          environmentName: detail.environmentName,
          executionStatus: detail.executionStatus as NonNullable<
            NonNullable<ScheduleCalendarEvent['resource']>['executionStatus']
          >,
          externalExecutionId: detail.externalExecutionId,
          isPaused: detail.isSchedulesPaused,
        },
      });
    };

    filteredDetails.forEach((detail, index) => {
      appendDetailEvent(detail, 'exec', index);
    });

    filteredPlannedDetails.forEach((detail, index) => {
      appendDetailEvent(detail, 'planned', index);
    });

    return events;
  }, [filteredSchedules, filteredDetails, filteredPlannedDetails, timeMode]);

  // Event styling
  const eventStyleGetter = useCallback((event: ScheduleCalendarEvent) => {
    const status = event.resource?.isPaused
      ? 'Paused'
      : event.resource?.executionStatus || 'default';
    const colors = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.default;
    const isDashed =
      status === 'Paused' || (status === 'Scheduled' && !event.resource?.externalExecutionId);

    return {
      style: {
        backgroundColor: colors.background,
        borderLeft: `4px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '4px',
        padding: '2px 4px',
        fontSize: '0.75rem',
        borderStyle: isDashed ? 'dashed' : 'solid',
      },
    };
  }, []);

  // Handle event click
  const handleSelectEvent = useCallback(
    (event: ScheduleCalendarEvent) => {
      onEventClick?.(event);
    },
    [onEventClick]
  );

  // Handle navigation
  const handleNavigate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Handle view change
  const handleViewChange = useCallback((view: View) => {
    setCurrentView(view);
  }, []);

  // Handle range change (when view changes or navigation happens)
  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      let start: Date;
      let end: Date;

      if (Array.isArray(range)) {
        [start] = range;
        end = range[range.length - 1];
      } else {
        ({ start, end } = range);
      }

      lastVisibleRangeRef.current = { start, end };
      emitApiRange(start, end);
    },
    [emitApiRange]
  );

  // Custom toolbar component
  const CustomToolbar = useCallback(
    ({
      label,
      onNavigate,
      onView,
      view,
    }: {
      label: string;
      onNavigate: (nav: string) => void;
      onView: (view: string) => void;
      view: string;
    }) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => onNavigate('TODAY')}>Today</Button>
          <Button onClick={() => onNavigate('PREV')}>Back</Button>
          <Button onClick={() => onNavigate('NEXT')}>Next</Button>
        </ButtonGroup>

        <Typography variant="h6" component="span">
          {label}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_e, next) => {
            if (next) onView(next);
          }}
        >
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="day">Day</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    ),
    []
  );

  if (isLoading) {
    return (
      <Paper sx={{ p: 2 }}>
        {/* Skeleton legend */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" width={80} height={20} />
          ))}
        </Stack>
        {/* Skeleton toolbar */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={160} height={32} />
          <Skeleton variant="rounded" width={120} height={28} />
          <Skeleton variant="rounded" width={160} height={32} />
        </Stack>
        {/* Skeleton calendar grid */}
        <Skeleton variant="rounded" width="100%" height={540} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {legendStatuses.map((status) => {
          const colors = STATUS_COLORS[status];
          const isDashedLegend = status === 'Scheduled';
          return (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: colors.background,
                  borderLeft: `3px solid ${colors.border}`,
                  borderRadius: 0.5,
                  borderStyle: isDashedLegend ? 'dashed' : 'solid',
                }}
              />
              <Typography variant="caption">{status}</Typography>
            </Box>
          );
        })}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {timeMode === 'utc' ? 'Times shown in UTC' : 'Times shown in your local timezone'}
        </Typography>
      </Box>

      {/* Calendar */}
      <Box
        sx={{
          height: 'calc(100vh - 320px)',
          minHeight: 600,
          // Constrain the all-day events row so it can't grow tall enough to
          // push the time grid completely out of view when there are many
          // schedules in a single day. Without this cap, react-big-calendar
          // lets the all-day row expand indefinitely (it's `flex: 0 0 auto`).
          '& .rbc-time-view .rbc-allday-cell': {
            maxHeight: 140,
            overflowY: 'auto',
          },
          // Month view: when there are too many events to fit in a day cell,
          // show "+N more" instead of overflowing the row.
          '& .rbc-row-segment': {
            padding: '0 1px 1px 1px',
          },
          '& .rbc-show-more': {
            color: 'primary.main',
            fontWeight: 500,
          },
        }}
      >
        <Calendar
          key={timeMode}
          localizer={localizer}
          events={calendarEvents}
          formats={calendarFormats}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleSelectEvent}
          onRangeChange={handleRangeChange}
          eventPropGetter={eventStyleGetter}
          components={{
            toolbar: CustomToolbar,
          }}
          popup
          showMultiDayTimes
          style={{ height: '100%' }}
        />
      </Box>
    </Paper>
  );
};
